/**
 * SiteTypesPage — 86bc7raat. PLATFORM_ADMIN CRUD for the platform `site_types` catalog.
 *
 * Mirrors TankCatalogPage: table of all rows including inactive, an Active/Inactive
 * filter, Add / Edit via modal, and Deactivate/Reactivate through PATCH { isActive }.
 *
 * No delete, by design. `sites` and `tenant_site_type_settings` reference site_types
 * under ON DELETE RESTRICT, and migration 0089 grants INSERT + UPDATE only — so
 * deactivation is the removal path. Deactivating hides the type from new-site pickers
 * and leaves existing sites working, which is what the copy below tells the admin.
 *
 * The TENANT-level visibility toggle is deliberately NOT here: it lives in
 * web-dispatch, the tenant's own admin surface (Corbin's decision on this ticket).
 */
import * as React from 'react';
import { toast } from 'sonner';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'src/components/ui/table';
import { Button } from 'src/components/ui/button';
import { AddButton, Pill, subText } from '../../ui/bits';
import { useSiteTypes, useUpdateSiteType, type SiteType } from './hooks';
import { SiteTypeModal } from './SiteTypeModal';

type Filter = 'all' | 'active' | 'inactive';

/** Deactivate/reactivate a single row (its own hook instance, as in TankCatalogPage). */
function StatusAction({ siteType }: { siteType: SiteType }): React.JSX.Element {
  const update = useUpdateSiteType(siteType.id);

  const onDeactivate = (): void => {
    if (
      !window.confirm(
        `Deactivate "${siteType.label}"? Existing sites of this type keep working, but haulers will no longer see it when creating a new site.`,
      )
    ) {
      return;
    }
    update.mutate(
      { isActive: false },
      {
        onSuccess: () => toast.success('Site type deactivated'),
        onError: () => toast.error('Could not deactivate'),
      },
    );
  };
  const onReactivate = (): void => {
    update.mutate(
      { isActive: true },
      {
        onSuccess: () => toast.success('Site type reactivated'),
        onError: () => toast.error('Could not reactivate'),
      },
    );
  };

  return siteType.isActive ? (
    <Button
      type="button"
      variant="outline"
      disabled={update.isPending}
      onClick={onDeactivate}
      style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
    >
      Deactivate
    </Button>
  ) : (
    <Button type="button" variant="outline" disabled={update.isPending} onClick={onReactivate}>
      Reactivate
    </Button>
  );
}

export function SiteTypesPage(): React.JSX.Element {
  const { data, isLoading, isError } = useSiteTypes();
  const [filter, setFilter] = React.useState<Filter>('all');
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<SiteType | null>(null);

  const rows = React.useMemo(() => {
    const all = data?.siteTypes ?? [];
    if (filter === 'active') return all.filter((s) => s.isActive);
    if (filter === 'inactive') return all.filter((s) => !s.isActive);
    return all;
  }, [data, filter]);

  const openAdd = (): void => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (s: SiteType): void => {
    setEditing(s);
    setModalOpen(true);
  };

  const FILTERS: readonly { id: Filter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'active', label: 'Active' },
    { id: 'inactive', label: 'Inactive' },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Site Types</h2>
          <p style={{ ...subText, marginTop: '2px' }}>{rows.length} site types</p>
        </div>
        <AddButton label="Add Site Type" onClick={openAdd} />
      </div>

      <p style={{ ...subText, marginBottom: '16px' }}>
        The platform catalog of site types. Each tenant chooses which of these it uses from its own
        Settings screen — deactivating one here removes it for everybody.
      </p>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
        {FILTERS.map((f) => (
          <Button
            key={f.id}
            type="button"
            variant={filter === f.id ? 'default' : 'outline'}
            onClick={(): void => setFilter(f.id)}
            style={
              filter === f.id
                ? { backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }
                : undefined
            }
          >
            {f.label}
          </Button>
        ))}
      </div>

      {isLoading && <p style={subText}>Loading site types…</p>}
      {isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load the site types. Refresh to try again.
        </p>
      )}

      {!isLoading && !isError && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Label</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Sort order</TableHead>
              <TableHead>Status</TableHead>
              <TableHead style={{ textAlign: 'right' }}>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((s) => (
              <TableRow key={s.id}>
                <TableCell style={{ fontWeight: 500 }}>{s.label}</TableCell>
                <TableCell style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                  {s.code}
                </TableCell>
                <TableCell>{s.sortOrder}</TableCell>
                <TableCell>
                  <Pill
                    label={s.isActive ? 'Active' : 'Inactive'}
                    tone={s.isActive ? 'positive' : 'muted'}
                  />
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <Button type="button" variant="outline" onClick={(): void => openEdit(s)}>
                      Edit
                    </Button>
                    <StatusAction siteType={s} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} style={{ ...subText, textAlign: 'center', padding: '32px' }}>
                  No site types in this view.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <SiteTypeModal open={modalOpen} onOpenChange={setModalOpen} editing={editing} />
    </div>
  );
}
